import * as React from 'react'
import { Plus } from 'lucide-react'

import {
  ORG_DEFAULT_LABEL,
  RESET_TO_ORG_DEFAULT_LABEL,
  type MethodRow,
} from './authenticationDraft'
import { MethodRowEditor } from './MethodRowEditor'

export function MethodsEditor({
  rows,
  methodsOverridden,
  interactionDisabled,
  onUpdateRow,
  onRemoveRow,
  onAddRow,
  onResetToOrg,
  showInheritance = true,
}: {
  rows: MethodRow[]
  methodsOverridden: boolean
  interactionDisabled: boolean
  onUpdateRow: (idx: number, row: MethodRow) => void
  onRemoveRow: (idx: number) => void
  onAddRow: () => void
  onResetToOrg: () => void
  showInheritance?: boolean
}) {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-slate-700">Methods</h4>
        {showInheritance ? (
          <div className="flex items-center gap-2 text-xs text-slate-500">
            {methodsOverridden ? (
              <>
                <span className="rounded bg-amber-50 px-1.5 py-0.5 text-amber-700">
                  Overridden
                </span>
                {!interactionDisabled && (
                  <button
                    type="button"
                    onClick={onResetToOrg}
                    className="text-indigo-600 hover:text-indigo-800">
                    {RESET_TO_ORG_DEFAULT_LABEL}
                  </button>
                )}
              </>
            ) : (
              <span className="text-slate-400">{ORG_DEFAULT_LABEL}</span>
            )}
          </div>
        ) : null}
      </div>
      <p className="text-xs text-slate-500">
        Each row is a complete set of identity fields. Authentication succeeds
        when any one row's fields all match (or N of them, if configured).
      </p>
      <div className="flex flex-col gap-2">
        {rows.map((row, idx) => (
          <React.Fragment key={idx}>
            {idx > 0 && (
              <div className="flex items-center gap-3" aria-hidden="true">
                <div className="h-px flex-1 bg-slate-200" />
                <span className="text-xs font-medium uppercase tracking-wide text-slate-400">
                  or
                </span>
                <div className="h-px flex-1 bg-slate-200" />
              </div>
            )}
            <MethodRowEditor
              index={idx}
              row={row}
              disabled={interactionDisabled}
              onUpdateRow={(r) => onUpdateRow(idx, r)}
              onRemoveRow={() => onRemoveRow(idx)}
            />
          </React.Fragment>
        ))}
      </div>
      {!interactionDisabled && (
        <button
          type="button"
          onClick={onAddRow}
          className="flex w-full items-center justify-center gap-1 rounded-lg border border-dashed border-slate-200 py-2 text-sm text-slate-500 hover:border-slate-300 hover:text-slate-700">
          <Plus className="h-3.5 w-3.5" /> Add method
        </button>
      )}
    </div>
  )
}
